import {
  ACTION_CLOSE_QUESTIONS_FORM,
  ACTION_SUBMIT_QUESTIONS_FORM,
} from 'utils/analytics/eventTypeConstantsAmplitude';
import { Button, Dialog, DialogActions, DialogContent, DialogHeading, Typography } from 'omakase';
import { Formik } from 'formik';
import { LocalDateTime } from 'js-joda';
import {
  PreVisitQuestionsFields,
  convertTicketTypeQuestionsToFields,
  convertToAdditionalTextQuestions,
  convertToPurchaseQuestions,
  createValidationSchema,
} from '@tock/shared/utils/questionUtils';
import { ProfilePreVisitQuestionFormBody } from 'components/business/receipts/ReceiptPreVisitQuestionDialog/ProfilePreVisitQuestionFormBody';
import { QuestionsFormData } from 'forms/QuestionsForm';
import { ReceiptPreVisitQuestionFormBody } from 'components/business/receipts/ReceiptPreVisitQuestionDialog/ReceiptPreVisitQuestionFormBody';
import { assertNever } from '@tock/shared/utils/typeUtils';
import { consumer, core } from '@tock/coretypes';
import { flow } from 'lodash';
import { useAmplitudeSendEvent } from 'utils/analytics/AnalyticsContext';

interface Props {
  business: consumer.IBusinessConfig;
  isAuthenticated: boolean;
  mode: 'PROFILE' | 'RECEIPT';
  onClose(): void;
  purchase: core.PastPurchase;
  questions: core.TicketTypeQuestion[];
  questionnaireIntroText?: core.TicketTypeDetails['questionnaireIntroText'];
  onSubmitQuestionsForm: (fields: PartialFields<QuestionsFormData>) => Promise<unknown>;
}

export const serializeFormDataToPurchaseQuestions =
  (questions: core.TicketTypeQuestion[]) =>
  (values: PreVisitQuestionsFields): core.IPurchaseQuestion[] => {
    return convertToPurchaseQuestions(questions, values);
  };

export const ReceiptPreVisitQuestionDialog = ({
  business,
  isAuthenticated,
  mode,
  onClose,
  purchase,
  questions,
  questionnaireIntroText,
  onSubmitQuestionsForm,
}: React.PropsWithChildren<Props>) => {
  const amplitudeSendEvent = useAmplitudeSendEvent();
  const standardizedQuestions = convertToAdditionalTextQuestions(questions);

  const initialValues: PreVisitQuestionsFields =
    convertTicketTypeQuestionsToFields(standardizedQuestions);

  const validationSchema = createValidationSchema(standardizedQuestions);

  const pastDate = LocalDateTime.parse(purchase.ticketDateTime).isBefore(LocalDateTime.now());

  const getMessage = () => {
    if (pastDate) {
      return (
        <>
          <Typography variant="heading04">These questions have expired</Typography>
          <Typography variant="body02">
            These questions have expired. This reservation has already taken place. We hope you
            enjoyed your time at {business.name}.
          </Typography>
        </>
      );
    }

    if (standardizedQuestions.length < 1) {
      return (
        <>
          <Typography variant="heading04">No questions</Typography>
          <Typography variant="body02">
            If you're seeing this message it means that there aren't any questions to answer for
            this reservation. If you have any special needs, make sure to bring them up when you
            dine at {business.name}.
          </Typography>
        </>
      );
    }

    if (purchase.question.length > 0) {
      return (
        <>
          <Typography variant="heading04">Your responses have already been shared</Typography>
          <Typography variant="body02">
            If you have questions or need to make edits, please contact {business.name}.
          </Typography>
        </>
      );
    }

    if (purchase.isSurveyExpired) {
      return (
        <>
          <Typography variant="heading04">These questions have expired</Typography>
          <Typography variant="body02">
            It is too close to the time of your reservation to submit your responses as{' '}
            {business.name} needs adequate time to accommodate your needs. Please reach out to the
            restaurant with immediate concerns.
          </Typography>
        </>
      );
    }
    return null;
  };

  if (getMessage()) {
    return (
      <Dialog open={true} aria-labelledby="confirm-title" aria-describedby="confirm-description">
        <DialogHeading id="confirm-title" onClose={onClose}>
          Pre-visit questions
        </DialogHeading>
        <DialogContent id="confirm-description">{getMessage()}</DialogContent>
        <DialogActions>
          <Button size="medium" variant="primary" onClick={onClose} fullWidth>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  const trackSubmission = (questions: core.IPurchaseQuestion[]) => {
    const answeredQuestions = questions.filter((q) => {
      switch (q.type) {
        case 'TEXTAREA':
        case 'TEXTINPUT':
          return !!q.textResponse?.value;

        case 'CHECKBOXES':
          return (
            (q.checkboxResponse?.value && q.checkboxResponse?.value.length > 0) ||
            !!q.checkboxResponse?.otherValue
          );

        case 'RADIO_BUTTON':
          return !!q.radioResponse?.value || !!q.radioResponse?.otherValue;
        default:
          assertNever(q.type as never);
      }
    });

    amplitudeSendEvent(ACTION_SUBMIT_QUESTIONS_FORM, {
      questionCount: questions.length,
      answerCount: answeredQuestions.length,
    });

    return questions;
  };

  const handleCloseQuestionsForm = () => {
    amplitudeSendEvent(ACTION_CLOSE_QUESTIONS_FORM);
    onClose();
  };

  return (
    <Formik<PreVisitQuestionsFields>
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values: PreVisitQuestionsFields) => {
        const handleSubmission = flow(
          serializeFormDataToPurchaseQuestions(standardizedQuestions),
          trackSubmission,
          (questions) => ({ questions }),
          onSubmitQuestionsForm
        );

        return handleSubmission(values);
      }}
    >
      <>
        {mode === 'PROFILE' && (
          <ProfilePreVisitQuestionFormBody
            business={business}
            questions={standardizedQuestions}
            questionnaireIntroText={questionnaireIntroText}
            onClose={handleCloseQuestionsForm}
          />
        )}
        {mode === 'RECEIPT' && (
          <ReceiptPreVisitQuestionFormBody
            business={business}
            isAuthenticated={isAuthenticated}
            purchase={purchase}
            questions={standardizedQuestions}
            questionnaireIntroText={questionnaireIntroText}
            onClose={handleCloseQuestionsForm}
          />
        )}
      </>
    </Formik>
  );
};

<DialogHeading>Pre-visit questions</DialogHeading>;
